// CollapseItem.tsx
import React, { useRef, useState, useEffect } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useCollapseContext } from "./index";
import { ReactComponent as DefaultExpandIcon } from "@/assets/icon-arrow-right.svg";
import { classNames } from "@/utils";

interface CollapseItemProps {
  itemKey: string;
  title: React.ReactNode;
  children: React.ReactNode;
  expandIcon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

function useResizeObserver(ref: React.RefObject<HTMLElement>, onResize: () => void) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(() => onResize());
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, onResize]);
}

export const CollapseItem: React.FC<CollapseItemProps> = (props) => {
  const { itemKey, title, children, expandIcon, className, disabled } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const { activeKeys, onItemToggle } = useCollapseContext();
  const isOpen = activeKeys.includes(itemKey);
  const [contentHeight, setContentHeight] = useState(0);

  useResizeObserver(contentRef, () => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  });

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  const contentStyle = useSpring({
    height: isOpen ? contentHeight : 0,
    opacity: isOpen ? 1 : 0,
    config: { tension: 250, friction: 30 },
  });

  const arrowStyle = useSpring({
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  });

  return (
    <div
      className={classNames({
        [className]: className,
        "collapse-item": true,
        "collapse-item--disabled": disabled,
        "active": isOpen,
      })}
    >
      <div className="collapse-item__header" onClick={() => !disabled && onItemToggle(itemKey)}>
        <div>{title}</div>
        {!disabled && <animated.span style={arrowStyle}>{expandIcon || <DefaultExpandIcon />}</animated.span>}
      </div>
      <animated.div style={{ overflow: "hidden", ...contentStyle }}>
        <div className="collapse-item__content" ref={contentRef}>{children}</div>
      </animated.div>
    </div>
  );
};
